import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { StripeBankAccountStatus } from '@umss/shared/data-access';
import { BadgeVariant } from '@umss/shared/utils';
import { UmssBadgeComponent } from '../badge/badge.component';

interface StripeBadgeProps {
  label: string;
  variant: BadgeVariant;
  iconName?: string;
}

const STRIPE_STATUS_BADGE_PROPS: Record<
  StripeBankAccountStatus,
  StripeBadgeProps
> = {
  [StripeBankAccountStatus.NotLinked]: {
    label: 'Not linked',
    variant: 'neutral',
    iconName: 'phosphorWarningCircle'
  },
  [StripeBankAccountStatus.Linked]: {
    label: 'Linked to Stripe',
    variant: 'info',
    iconName: 'phosphorLink'
  },
  [StripeBankAccountStatus.PendingVerification]: {
    label: 'Verification pending',
    variant: 'warning',
    iconName: 'phosphorClock'
  },
  [StripeBankAccountStatus.Verified]: {
    label: 'Verified',
    variant: 'success',
    iconName: 'phosphorCheckCircle'
  },
  [StripeBankAccountStatus.Failed]: {
    label: 'Action needed',
    variant: 'error',
    iconName: 'phosphorXCircle'
  }
};

const NULL_BADGE_PROPS: StripeBadgeProps = {
  label: 'Not linked',
  variant: 'neutral'
};

@Component({
  selector: 'umss-stripe-bank-account-status-badge',
  standalone: true,
  imports: [UmssBadgeComponent],
  template: `
    @if (badgeProps) {
      <umss-badge
        [variant]="badgeProps.variant"
        [label]="badgeProps.label"
        [iconName]="badgeProps.iconName"
        [size]="size"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UmssStripeBankAccountStatusBadgeComponent {
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  @Input({ required: true }) set status(value: StripeBankAccountStatus | null) {
    this.badgeProps =
      value === null
        ? NULL_BADGE_PROPS
        : (STRIPE_STATUS_BADGE_PROPS[value] ?? NULL_BADGE_PROPS);
  }

  badgeProps: StripeBadgeProps | undefined;
}
